import React, { useEffect, useState, useContext } from "react";
import './landing_page.css';
import axios from "axios";
import { Fetchexecutequery } from "../../services/process/BRS1/brsService7";
import MRU from '../../assets/images/pontis.png';
import * as Icons from 'ionicons/icons';
import Select from "react-select";
import { Calendar, FileText, Send, Target, Activity, Bell } from "lucide-react";

// Navy + orange tokens, two tints derived from each.
const NAVY = { accent: '#0A2352', tint: '#E8ECF7' };
const ORANGE = { accent: '#F97316', tint: '#FFF1E6' };
const ACCENTS = [NAVY, ORANGE];

const T = {
  navy:     "#0a2352",
  navyMid:  "#143272",
  navyDark: "#061535",
  orange:   "#f97316",
  orangeL:  "#fb923c",
  bg:       "#f0f4fa",
  card:     "#ffffff",
  border:   "#dde4f0",
  text:     "#0a2352",
  muted:    "#5b6478",
  light:    "#f1f5f9",
  success:  "#16a34a",
  danger:   "#dc2626",
  warn:     "#d97706",
  purple:   "#7c3aed",
  accent:   "#2563eb",
  cyan:     "#0891b2",
};

//  Static side-menu (permanent, no sessionStorage dependency) 

//  Shared menu labels/icons + per-entity URL mapping 
const MENU_ITEMS = [
  { key: "institution-setup", iconName: "peopleOutline",    sideMenu: "Institution Setup" },
  { key: "create-event",      iconName: "duplicateOutline", sideMenu: "Create Event"      },
  { key: "applicant-review",  iconName: "albumsOutline",    sideMenu: "Applications"      },
];

const ENTITY_ROUTE_MAP = {
  1: {
    "form-responses": "form_response",
  },

  9: {
    "institution-setup": "eventplatform/profile-creation",
    "create-event":      "eventplatform/create-event",
    "applicant-review":  "eventplatform/applicant-review",
  },
  10: {
    "institution-setup": "eventplatform/entity10/profile-creation", // placeholder
    "create-event":      "eventplatform/entity10/create-event",     // placeholder
    "applicant-review":  "eventplatform/entity10/applicant-review", // placeholder
  },
  11: {
    "institution-setup": "eventplatform/entity11/profile-creation", // placeholder
    "create-event":      "eventplatform/entity11/create-event",     // placeholder
    "applicant-review":  "eventplatform/entity11/applicant-review", // placeholder
  },
  12: {
    "institution-setup": "eventplatform/us-university-profile",
    "create-event":      "eventplatform/application-submission",
    "applicant-review":  "eventplatform/applicant-review",
  },
};

// Which profile table to check for a given entity's division_id, to decide
// whether the institution setup is complete.
const PROFILE_TABLE_MAP = {
  9:  "school_profile",
  12: "foreign_university_profile",
};

const STATIC_SIDE_MENU_PROCESS_LABEL = "Pontis Acadexus";
const STATIC_SIDE_MENU = [
  {
    process: "Pontis Acadexus",
    entity_id: 1,
    pages: [
      {
        name: "form_response",
        iconName: "qrCodeOutline",
        sideMenu: "Form Responses",
      },
    ],
  },
  {
    process: "Pontis Acadexus",
    entity_id: 9,
    pages: [
      { name: "eventplatform/profile-creation", iconName: "peopleOutline",    sideMenu: "Institution Setup" },
      { name: "eventplatform/create-event",      iconName: "duplicateOutline", sideMenu: "Create Event"      },
      { name: "eventplatform/applicant-review",  iconName: "albumsOutline",    sideMenu: "Applications"      },
    ],
  },
  {
    process: "Pontis Acadexus",
    entity_id: 12,
    pages: [
      { name: "eventplatform/us-university-profile",   iconName: "peopleOutline",    sideMenu: "Institution Setup" },
      { name: "eventplatform/application-submission",  iconName: "duplicateOutline", sideMenu: "Events"      },
    ],
  },
];

const EMPTY = { kpis: [], activityFeed: [], alertFeed: [], drillDown: {} };

const fmt = (n) => Number(n || 0).toLocaleString();
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
const fmtDate = (v) => v ? new Date(v).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—";
const FONT = "'Inter', 'Segoe UI', system-ui, sans-serif";

// Font used by the "Institution Setup" style side-menu tiles (.lpg-tile / .lpg-tile-title)
// — the KPI strip labels/sub-labels below are matched to this same family.
const TILE_FONT = "'Plus Jakarta Sans', sans-serif";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 12,
    borderColor: state.isFocused ? '#0A2352' : '#D7DEEE',
    borderWidth: 1.5,
    boxShadow: 'none',
    minHeight: 44,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    '&:hover': { borderColor: '#0A2352' },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#0A2352' : state.isFocused ? '#E8ECF7' : '#fff',
    color: state.isSelected ? '#fff' : '#0A2352',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  }),
  singleValue: (base) => ({ ...base, color: '#0A2352', fontWeight: 600 }),
};

const Ctx = React.createContext(EMPTY);
const useDash = () => useContext(Ctx);

const Card = ({ children, style = {}, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: T.card,
      borderRadius: 14,
      border: `1px solid ${T.border}`,
      boxShadow: "0 1px 3px rgba(10, 35, 82, 0.05)",
      overflow: "hidden",
      fontFamily: FONT,
      ...style,
    }}
  >
    {children}
  </div>
);

//  Drill-down modal: shows the raw rows behind a KPI card 
function DrillDownModal({ drillKey, onClose }) {
  const { drillDown } = useDash();
  const cfg = drillDown[drillKey];
  if (!cfg) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(10, 21, 48, 0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.card, borderRadius: 16, width: "min(720px, 100%)",
          maxHeight: "80vh", display: "flex", flexDirection: "column",
          boxShadow: "0 20px 50px rgba(10,21,48,0.25)", fontFamily: FONT,
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 22px", borderBottom: `1px solid ${T.border}`,
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>{cfg.title}</div>
            <div style={{ fontSize: 12.5, color: T.muted, marginTop: 2 }}>{cfg.rows.length} record{cfg.rows.length === 1 ? "" : "s"}</div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: "none", background: T.light, borderRadius: 8, width: 30, height: 30,
              cursor: "pointer", fontSize: 16, color: T.muted, lineHeight: 1,
            }}
          >×</button>
        </div>

        <div style={{ overflowY: "auto", padding: "10px 22px 20px" }}>
          {cfg.rows.length === 0 ? (
            <p style={{ fontSize: 13, color: T.muted, padding: "20px 0" }}>No records found.</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {cfg.columns.map(col => (
                    <th key={col.key} style={{
                      textAlign: "left", padding: "8px 10px", color: T.muted,
                      fontWeight: 700, fontSize: 11.5, textTransform: "uppercase",
                      letterSpacing: "0.04em", borderBottom: `1px solid ${T.border}`,
                    }}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cfg.rows.map((row, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                    {cfg.columns.map(col => (
                      <td key={col.key} style={{ padding: "9px 10px", color: T.text }}>
                        {col.render ? col.render(row) : (row[col.key] ?? "—")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

//  Profile-incomplete modal: shown when the current entity's division_id
// has no matching row in its profile table (school_profile / foreign_university_profile) 
function ProfileIncompleteModal({ onGoToSetup }) {
  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(10, 21, 48, 0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1100, padding: 24,
      }}
    >
      <div
        style={{
          background: T.card, borderRadius: 16, width: "min(440px, 100%)",
          boxShadow: "0 20px 50px rgba(10,21,48,0.25)", fontFamily: FONT,
          padding: "28px 26px 24px", textAlign: "center",
        }}
      >
        <div style={{
          width: 52, height: 52, borderRadius: "50%", background: NAVY.tint,
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 16px",
        }}>
          <ion-icon icon={Icons.alertCircleOutline} style={{ fontSize: 26, color: T.navy }}></ion-icon>
        </div>
        <div style={{ fontSize: 17, fontWeight: 800, color: T.text, marginBottom: 8 }}>
          Complete Your Institution Profile
        </div>
        <p style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.5, margin: "0 0 22px" }}>
          We couldn't find a completed institution profile for your account. Please
          complete the institution setup before continuing.
        </p>
        <button
          onClick={onGoToSetup}
          style={{
            border: "none", background: T.navy, color: "#fff", borderRadius: 10,
            padding: "11px 22px", fontSize: 14, fontWeight: 700, cursor: "pointer",
            fontFamily: FONT, width: "100%",
          }}
        >
          Complete Profile Now
        </button>
      </div>
    </div>
  );
}

function KPIStrip() {
  const { kpis } = useDash();
  const [openKey, setOpenKey] = useState(null);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
      {kpis.map(k => (
        <Card
          key={k.label}
          onClick={k.drillKey ? () => setOpenKey(k.drillKey) : undefined}
          style={{
            padding: "18px 22px",
            borderLeft: `4px solid ${k.color}`,
            cursor: k.drillKey ? "pointer" : "default",
            transition: "box-shadow 0.15s ease, transform 0.15s ease",
          }}
        >
          {/* Top row: icon + label on left, value on right */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
            {/* Left: icon + label stacked */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{
                background: k.color + "16",
                borderRadius: 7,
                width: 28,
                height: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}>
                <k.icon size={14} color={k.color} strokeWidth={2.25} />
              </div>
              <span style={{
                fontSize: 12,
                color: T.muted,
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                fontFamily: TILE_FONT,
              }}>{k.label}</span>
            </div>

            {/* Right: big number */}
            <div style={{
              fontSize: 52,
              fontWeight: 800,
              color: T.text,
              letterSpacing: "-0.03em",
              lineHeight: 1,
              textAlign: "right",
            }}>
              {k.value}
            </div>
          </div>

          {/* Sub-label below — same family as the tile titles, bumped up in size */}
          <div style={{ fontSize: 13.5, color: T.muted, fontWeight: 600, marginTop: 10, fontFamily: TILE_FONT }}>{k.sub}</div>
        </Card>
      ))}

      {openKey && <DrillDownModal drillKey={openKey} onClose={() => setOpenKey(null)} />}
    </div>
  );
}

function build({ events = [], applications = [], invitations = [], entityId } = {}) {
  const totalApps  = applications.length;
  const totalInv   = invitations.length;
  const totalAcc   = invitations.filter(r => r.invitation_status === "accepted").length;
  const activeEvts = events.filter(r => ["registration_open", "published"].includes(r.event_status)).length;
  const isEntity12 = Number(entityId) === 12;
  const fillRate   = isEntity12
    ? (totalInv ? pct(totalAcc, totalInv) : 0)
    : (totalApps ? pct(totalAcc, totalApps) : 0);

  const activityFeed = [
    ...applications.slice(0, 5).map(r => ({
      dot:   T.accent,
      actor: r.school_name || "Unknown School",
      sub:   `Applied · ${r.event_name}`,
      time:  fmtDate(r.applied_on),
    })),
    ...invitations.filter(r => r.invitation_status === "accepted").slice(0, 4).map(r => ({
      dot:   T.success,
      actor: r.school_name || "Unknown School",
      sub:   `Accepted · ${r.event_name}`,
      time:  fmtDate(r.responded_on),
    })),
  ].slice(0, 8);

  const alertFeed = events
    .filter(e => e.reg_close_date)
    .map(e => ({
      dot:   T.danger,
      actor: `Deadline: ${fmtDate(e.reg_close_date)}`,
      sub:   e.event_name,
      time:  "Upcoming",
    }))
    .slice(0, 5);

  const kpis = isEntity12
    ? [
        { label: "Total Events",            value: fmt(activeEvts),    sub: "active events",                 icon: Calendar, color: T.accent,  drillKey: "events"       },
        { label: "Applied by me",           value: fmt(totalApps),     sub: "applications submitted",        icon: FileText, color: T.orange,  drillKey: "applications" },
        { label: "Invitations Received",    value: fmt(totalInv),      sub: "invitations received",          icon: Send,     color: T.cyan,    drillKey: "invitations"  },
        { label: "Confirmations",           value: `${fillRate}%`,     sub: `${fmt(totalAcc)} confirmed`,    icon: Target,   color: T.success, drillKey: "confirmations"},
      ]
    : [
        { label: "Events Hosted by me", value: String(activeEvts), sub: `of ${events.length} total`, icon: Calendar, color: T.accent,  drillKey: "events"       },
        { label: "University Interests",  value: fmt(totalApps),     sub: "total received",            icon: FileText, color: T.orange,  drillKey: "applications" },
        { label: "Universities Shortlisted by me",   value: fmt(totalInv),      sub: "Invitations sent to universities",            icon: Send,     color: T.cyan,    drillKey: "invitations"  },
        { label: "Confirmations Received",     value: `${fillRate}%`,     sub: `${fmt(totalAcc)} accepted`,  icon: Target,   color: T.success, drillKey: "confirmations"},
      ];

  // Drill-down datasets — built for every entity, not just entity_id 12.
  // Both branches share the same drillKeys ("events" / "applications" /
  // "invitations" / "confirmations") so KPIStrip's onClick wiring works
  // identically regardless of which entity is active; only the titles and
  // column labels differ to reflect the host vs. applicant perspective.
  const drillDown = isEntity12 ? {
    events: {
      title: "Active Events",
      rows: events.filter(r => ["registration_open", "published"].includes(r.event_status)),
      columns: [
        { key: "event_name",      label: "Event" },
        { key: "event_status",    label: "Status" },
        { key: "reg_close_date",  label: "Reg. Closes", render: r => fmtDate(r.reg_close_date) },
      ],
    },
    applications: {
      title: "Applications Submitted by Me",
      rows: applications,
      columns: [
        { key: "event_name",  label: "Event" },
        { key: "school_name", label: "Host" },
        { key: "applied_on",  label: "Applied On", render: r => fmtDate(r.applied_on) },
      ],
    },
    invitations: {
      title: "Invitations Received",
      rows: invitations,
      columns: [
        { key: "event_name",         label: "Event" },
        { key: "school_name",        label: "Host" },
        { key: "invitation_status",  label: "Status" },
        { key: "responded_on",       label: "Responded On", render: r => fmtDate(r.responded_on) },
      ],
    },
    confirmations: {
      title: "Invitations Confirmed by Me",
      rows: invitations.filter(r => r.invitation_status === "accepted"),
      columns: [
        { key: "event_name",   label: "Event" },
        { key: "school_name",  label: "Host" },
        { key: "responded_on", label: "Confirmed On", render: r => fmtDate(r.responded_on) },
      ],
    },
  } : {
    events: {
      title: "Events Hosted by Me",
      rows: events.filter(r => ["registration_open", "published"].includes(r.event_status)),
      columns: [
        { key: "event_name",      label: "Event" },
        { key: "event_status",    label: "Status" },
        { key: "reg_close_date",  label: "Reg. Closes", render: r => fmtDate(r.reg_close_date) },
      ],
    },
    applications: {
      title: "University Interests Received",
      rows: applications,
      columns: [
        { key: "event_name",  label: "Event" },
        { key: "school_name", label: "University" },
        { key: "applied_on",  label: "Applied On", render: r => fmtDate(r.applied_on) },
      ],
    },
    invitations: {
      title: "Invitations Sent to Universities",
      rows: invitations,
      columns: [
        { key: "event_name",         label: "Event" },
        { key: "school_name",        label: "University" },
        { key: "invitation_status",  label: "Status" },
        { key: "responded_on",       label: "Responded On", render: r => fmtDate(r.responded_on) },
      ],
    },
    confirmations: {
      title: "Confirmed Invitations",
      rows: invitations.filter(r => r.invitation_status === "accepted"),
      columns: [
        { key: "event_name",   label: "Event" },
        { key: "school_name",  label: "University" },
        { key: "responded_on", label: "Confirmed On", render: r => fmtDate(r.responded_on) },
      ],
    },
  };

  return {
    kpis,
    activityFeed,
    alertFeed,
    drillDown,
  };
}

function DashboardProvider({ children }) {
  const uc = JSON.parse(sessionStorage.getItem("userCredentials") || "{}");
  const [data, setData] = useState(EMPTY);

useEffect(() => {
    (async () => {
      try {
        const creds      = Fetchexecutequery();
        const query       = async (sql) => (await axios.post(creds[1], { query: sql }, creds[0])).data || [];
        const entityId    = uc.selectedentity || uc.entities_id;
        const divisionId  = uc.division[0] || uc.division_id;
        const tenantId    = uc.tenants_id;
        const userId      = uc.user_id || uc.userid || uc.id; // adjust key to match your session shape if different
        const isEntity12  = Number(entityId) === 12;

        let eventRows, applicationRows, invitationRows;
        

        if (isEntity12) {
          // Entity 12 (university) is the applicant, not the host — so events are
          // NOT filtered by creator_entity_id/division_id (that's the host's own division),
          // and applications/invitations are scoped to this user via applicant_user_id.
          [eventRows, applicationRows, invitationRows] = await Promise.all([
            query(`
              SELECT e.primary_id, e.event_status, e.event_name, e.reg_close_date
              FROM edu_platform.events e
              WHERE e.is_active = 1
                AND e.tenant_id = ${tenantId}
            `),
            query(`
              SELECT ea.primary_id, ea.applied_on, e.event_name, sp.school_name
              FROM edu_platform.event_applications ea
              JOIN edu_platform.events e ON e.primary_id = ea.event_id
              LEFT JOIN edu_platform.school_profile sp ON sp.primary_id = e.division_id AND sp.is_active = 1
              WHERE ea.is_active = 1
                AND ea.applicant_user_id = ${userId}
                AND e.tenant_id = ${tenantId}
              ORDER BY ea.applied_on DESC
            `),
            query(`
              SELECT ei.primary_id, ei.invitation_status, ei.responded_on, e.event_name, sp.school_name
              FROM edu_platform.event_invitations ei
              JOIN edu_platform.events e ON e.primary_id = ei.event_id
              LEFT JOIN edu_platform.school_profile sp ON sp.primary_id = e.division_id AND sp.is_active = 1
              WHERE ei.is_active = 1
                AND ei.applicant_user_id = ${userId}
                AND e.tenant_id = ${tenantId}
              ORDER BY ei.responded_on DESC
            `),
          ]);
        } else {
          [eventRows, applicationRows, invitationRows] = await Promise.all([
            query(`
              SELECT e.primary_id, e.event_status, e.event_name, e.reg_close_date
              FROM edu_platform.events e
              WHERE e.is_active = 1
                AND e.creator_entity_id = ${entityId}
                AND e.division_id = ${divisionId}
                AND e.tenant_id = ${tenantId}
            `),
            query(`
              SELECT ea.primary_id, ea.applied_on, e.event_name, sp.school_name
              FROM edu_platform.event_applications ea
              JOIN edu_platform.events e ON e.primary_id = ea.event_id
              LEFT JOIN edu_platform.school_profile sp ON sp.primary_id = e.division_id AND sp.is_active = 1
              WHERE ea.is_active = 1
                AND e.creator_entity_id = ${entityId}
                AND e.division_id = ${divisionId}
                AND e.tenant_id = ${tenantId}
              ORDER BY ea.applied_on DESC
            `),
            query(`
              SELECT ei.primary_id, ei.invitation_status, ei.responded_on, e.event_name, sp.school_name
              FROM edu_platform.event_invitations ei
              JOIN edu_platform.events e ON e.primary_id = ei.event_id
              LEFT JOIN edu_platform.school_profile sp ON sp.primary_id = e.division_id AND sp.is_active = 1
              WHERE ei.is_active = 1
                AND e.creator_entity_id = ${entityId}
                AND e.division_id = ${divisionId}
                AND e.tenant_id = ${tenantId}
              ORDER BY ei.responded_on DESC
            `),
          ]);
        }

        setData(build({
          events:       eventRows,
          applications: applicationRows,
          invitations:  invitationRows,
          entityId,
        }));
      } catch (err) {
        console.error("Dashboard load failed:", err);
      }
    })();
  }, []);

  return <Ctx.Provider value={data}>{children}</Ctx.Provider>;
}

function FeedList({ items, emptyLabel }) {
  if (items.length === 0) {
    return <p style={{ fontSize: 12.5, color: T.muted, margin: 0 }}>{emptyLabel}</p>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {items.map((it, i) => (
        <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: it.dot, marginTop: 5, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {it.actor}
            </div>
            <div style={{ fontSize: 11.5, color: T.muted, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {it.sub}
            </div>
          </div>
          <div style={{ fontSize: 11, color: T.muted, opacity: 0.7, whiteSpace: "nowrap", flexShrink: 0 }}>{it.time}</div>
        </div>
      ))}
    </div>
  );
}

function ActivityAlertsCard() {
  const { activityFeed, alertFeed } = useDash();
  const [tab, setTab] = useState("activity");

  const tabBtn = (active) => ({
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    padding: "7px 0",
    borderRadius: 7,
    border: "none",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.02em",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    background: active ? "#fff" : "transparent",
    color: active ? T.navy : T.muted,
    boxShadow: active ? "0 1px 2px rgba(10, 35, 82, 0.10)" : "none",
  });

  return (
    <Card style={{ padding: "16px 18px", height: 467 }}>
      <div style={{ display: "flex", gap: 4, background: T.light, borderRadius: 9, padding: 3, marginBottom: 14 }}>
        <button style={tabBtn(tab === "activity")} onClick={() => setTab("activity")}>
          <Activity size={13} /> Activity
        </button>
        <button style={tabBtn(tab === "alerts")} onClick={() => setTab("alerts")}>
          <Bell size={13} /> Alerts
        </button>
      </div>
      {tab === "activity" ? (
        <FeedList items={activityFeed} emptyLabel="No recent activity yet." />
      ) : (
        <FeedList items={alertFeed} emptyLabel="No upcoming deadlines." />
      )}
    </Card>
  );
}

//  Page header: shows the division name for the currently selected entity 
// Tries a few common session-object shapes; falls back to a live lookup
// against the same profile table used for the completeness check, and
// finally to a generic label if nothing is found.
function PageHeader({ activeEntityId, userCredentials }) {
  const [divisionName, setDivisionName] = useState(
    userCredentials.division_name ||
    userCredentials.divisionName ||
    userCredentials.school_name ||
    userCredentials.university_name ||
    userCredentials.institution_name ||
    null
  );

  useEffect(() => {
    if (divisionName) return; // already have a name from session
    let cancelled = false;

    (async () => {
      const table = PROFILE_TABLE_MAP[activeEntityId];
      const divisionId = userCredentials.division?.[0] || userCredentials.division_id;
      if (!table || !divisionId) return;

      try {
        const creds = Fetchexecutequery();
        // school_profile uses `school_name`; foreign_university_profile is assumed to
        // use `university_name` — adjust this column if your schema names it differently.
        const nameCol = table === "school_profile" ? "school_name" : "university_name";
        const sql = `
          SELECT ${nameCol} AS division_name
          FROM edu_platform.${table}
          WHERE division_id = ${divisionId}
            AND is_active = 1
          LIMIT 1
        `;
        const res = await axios.post(creds[1], { query: sql }, creds[0]);
        const rows = res.data || [];
        if (!cancelled && rows.length > 0 && rows[0].division_name) {
          setDivisionName(rows[0].division_name);
        }
      } catch (err) {
        console.error("Division name lookup failed:", err);
      }
    })();

    return () => { cancelled = true; };
  }, [activeEntityId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ marginBottom: 20 }}>
      <h1 className="lpg-page-title">{divisionName || "Institution Dashboard"}</h1>
    </div>
  );
}

const LandingPageGrid = () => {
  const [userCredentials] = useState(JSON.parse(sessionStorage.getItem("userCredentials") || "{}"));

  //  Entity selector (kept for multi-entity users, but side menu is now static) 
  const [entityOptions, SetEntityOptions]           = useState([]);
  const [selectedEntityOptions, SetselectedEntityOptions] = useState(null);

  //  Institution-profile completeness check (school_profile / foreign_university_profile) 
  const [profileCheckDone, setProfileCheckDone] = useState(false);
  const [profileMissing, setProfileMissing]     = useState(false);

  useEffect(() => {
    const wrapper1 = document.querySelector('.wrapper1');
    if (wrapper1) {
      wrapper1.style.transform = 'scale(1)';
      wrapper1.style.transformOrigin = 'center center';
      wrapper1.style.height = '126vh';
    }
    document.body.style.backgroundColor = '#EEF2FA';
    document.documentElement.style.backgroundColor = '#EEF2FA';
  }, []);

  useEffect(() => {
    const options = userCredentials.entity_id_options || [];
    SetEntityOptions(options);

    if (options.length > 0) {
      const existingSelectedEntity = userCredentials.selectedentity;
      if (existingSelectedEntity) {
        const selectedOption = options.find(opt => opt.value === existingSelectedEntity);
        if (selectedOption) SetselectedEntityOptions(selectedOption);
      } else {
        SetselectedEntityOptions(options[0]);
        const updated = { ...userCredentials, selectedentity: options[0].value };
        sessionStorage.setItem("userCredentials", JSON.stringify(updated));
      }
    }
  }, []);

  const handleInputChange = (data) => {
    SetselectedEntityOptions(data);
    const updated = { ...userCredentials, selectedentity: data.value };
    sessionStorage.setItem("userCredentials", JSON.stringify(updated));
    window.location.reload();
  };

  const handleRedirect = (url) => {
    window.location.href = `/${url}`;
  };

  // Filter the static menu by selected entity (defaults to entity_id 1 when no entity selector is present)
  const activeEntityId = selectedEntityOptions?.value ?? 1;
  const filteredGroups = STATIC_SIDE_MENU.filter(g => g.entity_id === activeEntityId);

  //  Check whether the institution profile is complete for this entity's division_id 
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const table = PROFILE_TABLE_MAP[activeEntityId];

      // Entities without a mapped profile table skip the check entirely.
      if (!table) {
        if (!cancelled) {
          setProfileMissing(false);
          setProfileCheckDone(true);
        }
        return;
      }

      try {
        const creds     = Fetchexecutequery();
        const divisionId = userCredentials.division?.[0] || userCredentials.division_id;

        if (!divisionId) {
          if (!cancelled) {
            setProfileMissing(true);
            setProfileCheckDone(true);
          }
          return;
        }

        const sql = `
          SELECT primary_id
          FROM edu_platform.${table}
          WHERE division_id = ${divisionId}
            AND is_active = 1
          LIMIT 1
        `;
        const res = await axios.post(creds[1], { query: sql }, creds[0]);
        const rows = res.data || [];

        if (!cancelled) {
          setProfileMissing(rows.length === 0);
          setProfileCheckDone(true);
        }
      } catch (err) {
        console.error("Profile completeness check failed:", err);
        // Fail open: don't block the user on a network/query error.
        if (!cancelled) {
          setProfileMissing(false);
          setProfileCheckDone(true);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [activeEntityId]);

  const handleGoToSetup = () => {
    const route = ENTITY_ROUTE_MAP[activeEntityId]?.["institution-setup"];
    if (route) handleRedirect(route);
  };

  return (
    <div className="wrapper1" style={{ padding: '28px 24px', boxSizing: 'border-box' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@500;600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

        .lpg-section + .lpg-section { margin-top: 22px; }
        .lpg-section-label {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #6B7592;
          margin: 0 0 12px 2px;
        }
        .lpg-tile-row {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .lpg-tile {
          flex: 0 0 auto;
          width: 100%;
          max-width: 260px;
          min-height: 110px;
          border-radius: 20px;
          padding: 18px 18px 16px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          cursor: pointer;
          border: none;
          text-align: left;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
        .lpg-tile:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 22px rgba(10, 35, 82, 0.14);
        }
        .lpg-tile-title {
          font-size: 17px;
          font-weight: 700;
          line-height: 1.25;
          margin: 0;
        }
        .lpg-tile-subtitle {
          font-size: 12px;
          font-weight: 500;
          opacity: 0.7;
          margin-top: 4px;
        }
        .lpg-tile-icon {
          margin-top: 18px;
          font-size: 24px;
          align-self: flex-start;
        }
        .lpg-page-title {
          font-family: 'Fraunces', serif;
          font-size: 28px;
          font-weight: 600;
          color: #0A2352;
          margin: 0;
          text-align: center;
        }
      `}</style>

      <PageHeader activeEntityId={activeEntityId} userCredentials={userCredentials} />

      <DashboardProvider>
        {/* KPI strip — top, full width */}
        <div style={{ marginBottom: 18 }}>
          <KPIStrip />
        </div>

        {/* Process tiles (left) + Activity/Alerts (right) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 240px', gap: 16, alignItems: 'start' }}>
          <Card style={{ padding: '22px 24px' }}>
            <div style={{ display: 'flex', gap: 28, alignItems: 'stretch' }}>
              <div style={{ flexShrink: 0, width: 280 }}>
                {entityOptions.length > 1 && (
                  <div style={{ marginBottom: 24 }}>
                    <Select
                      options={entityOptions}
                      value={selectedEntityOptions}
                      onChange={handleInputChange}
                      styles={selectStyles}
                      isSearchable={false}
                    />
                  </div>
                )}

                {filteredGroups.map((menuGroup, groupIndex) => {
                  const groupTitle  = menuGroup.process;
                  const menuItems   = menuGroup.pages;
                  const groupAccent = ACCENTS[groupIndex % ACCENTS.length];

                  return (
                    <div className="lpg-section" key={groupIndex}>
                      <p className="lpg-section-label">{groupTitle}</p>
                      <div className="lpg-tile-row">
                        {Array.isArray(menuItems) &&
                          menuItems.map((item, idx) => {
                            const featured = idx === 0;
                            const bg = featured ? groupAccent.accent : groupAccent.tint;
                            const fg = featured ? '#FFFFFF' : groupAccent.accent;

                            return (
                              <button
                                key={idx}
                                className="lpg-tile"
                                style={{ background: bg, color: fg }}
                                onClick={() => handleRedirect(item.name)}
                              >
                                <div>
                                  <p className="lpg-tile-title" style={{ color: fg }}>
                                    {item.sideMenu || item.name}
                                  </p>
                                </div>
                                <span className="lpg-tile-icon">
                                  <ion-icon
                                    icon={Icons[item.iconName] || Icons.documentTextOutline}
                                    style={{ color: fg }}
                                  ></ion-icon>
                                </span>
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ flex: 1, borderRadius: 16, overflow: 'hidden', minHeight: 320 }}>
                <img src={MRU} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </div>
            </div>
          </Card>

          <ActivityAlertsCard />
        </div>
      </DashboardProvider>

      {profileCheckDone && profileMissing && (
        <ProfileIncompleteModal onGoToSetup={handleGoToSetup} />
      )}
    </div>
  );
};

export default LandingPageGrid;